import type Alpine from 'alpinejs';

type AssetDescriptor = {
    id: string;
    src: string;
    preloads?: string[];
};

type AssetErrorDetail = {
    asset: AssetDescriptor;
    error: Error;
};

declare global {
    interface DocumentEventMap {
        'ui:asset:error': CustomEvent<AssetErrorDetail>;
    }
}

type AssetRegistrar = (AlpineInstance: Alpine.Alpine) => void;
type AssetModule = {default?: AssetRegistrar};
type AssetImporter = (src: string) => Promise<AssetModule>;
type AlpineWindow = Window & typeof globalThis & {Alpine?: Alpine.Alpine};

type AssetRecord = {
    src: string;
    promise: Promise<AssetRegistrar>;
    registered: WeakSet<object>;
};

const records = new Map<string, AssetRecord>();
const preloadUrls = new Set<string>();
const coveredSources = new Map<string, string>();
let registeredAssets = new WeakMap<object, Set<string>>();
let importer: AssetImporter = (src) => import(/* @vite-ignore */ src);

const assetError = (descriptor: AssetDescriptor, error: unknown): Error => {
    const reason = error instanceof Error ? error : new Error(String(error));
    document.dispatchEvent(new CustomEvent('ui:asset:error', {
        detail: {asset: descriptor, error: reason} satisfies AssetErrorDetail,
    }));
    return reason;
};

const preload = (url: string): void => {
    if (preloadUrls.has(url)) return;
    preloadUrls.add(url);
    const link = document.createElement('link');
    link.rel = 'modulepreload';
    link.href = url;
    document.head.append(link);
};

const assertCompatible = (descriptor: AssetDescriptor, record: AssetRecord): void => {
    if (record.src === descriptor.src) return;
    throw new Error(
        `Trellis asset ${descriptor.id} changed URL within one document: ${record.src} != ${descriptor.src}`,
    );
};

const registeredIds = (AlpineInstance: Alpine.Alpine): Set<string> => {
    const existing = registeredAssets.get(AlpineInstance);
    if (existing) return existing;
    const ids = new Set<string>();
    registeredAssets.set(AlpineInstance, ids);
    return ids;
};

const assertCoveredSourceCompatible = (descriptor: AssetDescriptor): void => {
    const existing = coveredSources.get(descriptor.id);
    if (existing === undefined) {
        coveredSources.set(descriptor.id, descriptor.src);
        return;
    }
    if (existing !== descriptor.src) {
        throw new Error(
            `Trellis asset ${descriptor.id} changed URL within one document: ${existing} != ${descriptor.src}`,
        );
    }
};

const registerAsset = (
    descriptor: AssetDescriptor,
    registrar: AssetRegistrar,
    record: AssetRecord,
    AlpineInstance: Alpine.Alpine,
): void => {
    if (record.registered.has(AlpineInstance)) return;
    try {
        registrar(AlpineInstance);
    } catch (error) {
        throw assetError(descriptor, error);
    }
    record.registered.add(AlpineInstance);
    registeredIds(AlpineInstance).add(descriptor.id);
};

const load = (descriptor: AssetDescriptor): Promise<AssetRegistrar> => {
    const existing = records.get(descriptor.id);
    if (existing) {
        try {
            assertCompatible(descriptor, existing);
        } catch (error) {
            return Promise.reject(assetError(descriptor, error));
        }
        return existing.promise;
    }

    descriptor.preloads?.forEach(preload);
    const promise = importer(descriptor.src)
        .then((module) => {
            if (typeof module.default !== 'function') {
                throw new TypeError(`Trellis asset ${descriptor.id} has no default registrar`);
            }
            return module.default;
        })
        .catch((error) => {
            records.delete(descriptor.id);
            throw assetError(descriptor, error);
        });
    records.set(descriptor.id, {src: descriptor.src, promise, registered: new WeakSet()});
    return promise;
};

const loadAssets = async (
    descriptors: AssetDescriptor[],
    AlpineInstance: Alpine.Alpine,
): Promise<void> => {
    const satisfied = registeredIds(AlpineInstance);
    const registrars = descriptors.map((descriptor) => {
        if (!satisfied.has(descriptor.id) || records.has(descriptor.id)) {
            return load(descriptor);
        }
        try {
            assertCoveredSourceCompatible(descriptor);
        } catch (error) {
            return Promise.reject(assetError(descriptor, error));
        }
        return null;
    });
    for (const [index, registrarPromise] of registrars.entries()) {
        if (registrarPromise === null) continue;
        const registrar = await registrarPromise;
        const descriptor = descriptors[index]!;
        const record = records.get(descriptor.id);
        if (!record) continue;
        registerAsset(descriptor, registrar, record, AlpineInstance);
    }
};

const prepareInitialAssets = (
    descriptors: AssetDescriptor[],
    registrars: AssetRegistrar[],
): void => {
    if (descriptors.length !== registrars.length) {
        throw new TypeError('Trellis initial asset descriptors and registrars must align');
    }

    descriptors.forEach((descriptor, index) => {
        const registrar = registrars[index];
        if (!registrar) throw new TypeError(`Trellis asset ${descriptor.id} has no default registrar`);
        const existing = records.get(descriptor.id);
        if (existing) {
            assertCompatible(descriptor, existing);
            return;
        }
        records.set(descriptor.id, {
            src: descriptor.src,
            promise: Promise.resolve(registrar),
            registered: new WeakSet(),
        });
    });

    document.addEventListener('alpine:init', () => {
        const AlpineInstance = (window as AlpineWindow).Alpine;
        if (!AlpineInstance) throw new Error('Trellis requires window.Alpine during alpine:init');
        registrars.forEach((registrar, index) => {
            const descriptor = descriptors[index]!;
            const record = records.get(descriptor.id);
            if (!record) return;
            registerAsset(descriptor, registrar, record, AlpineInstance);
        });
    }, {once: true});
};

const markAssetsRegistered = (
    logicalIds: readonly string[],
    AlpineInstance: Alpine.Alpine,
): void => {
    const satisfied = registeredIds(AlpineInstance);
    logicalIds.forEach((logicalId) => {
        satisfied.add(logicalId);
        records.get(logicalId)?.registered.add(AlpineInstance);
    });
};

const setAssetImporter = (next: AssetImporter): void => {
    importer = next;
};

const resetLoadedAssets = (): void => {
    records.clear();
    preloadUrls.clear();
    coveredSources.clear();
    registeredAssets = new WeakMap();
    importer = (src) => import(/* @vite-ignore */ src);
};

export {
    loadAssets,
    markAssetsRegistered,
    prepareInitialAssets,
    resetLoadedAssets,
    setAssetImporter,
};
export type {AssetDescriptor, AssetErrorDetail, AssetImporter, AssetRegistrar};
